import { date } from 'quasar'
import Model, { ID, Integer, IsoDateTime } from 'src/models/model'
import BookingWeek from 'src/models/schedule/BookingWeek'
import BookingWeekMovie from 'src/models/schedule/BookingWeekMovie'
import TimeSlot, { timeToMinutes } from 'src/models/schedule/TimeSlot'
import Screen from 'src/models/screen/Screen'
import Showing from 'src/models/showing/Showing'

export const minutesPerHour = 60

export const innerItemHeight = 90
export const yMargin = 10
export const totalItemHeight = innerItemHeight + yMargin

export const headerHeight = 60

export const defaultMovieMinutes = 90
export const defaultCleanUpMinutes = 10
export const defaultPreviewMinutes = 10

export default class ShowingSlot extends Model {
  static name = 'ShowingSlot'

  static employeeRequiredPermissionCategories = [Model.permissionCategories.FilmProgramming]

  static define () {
    this.fields = {
      id: {
        type: ID
      },

      bookingWeek: {
        type: BookingWeek,
        reference: true
      },

      timeSlot: {
        type: TimeSlot,
        customGetter: 'timeSlotGetter'
      },
      timeSlotId: {
        type: ID
      },
      screen: {
        type: Screen,
        customGetter: 'screenGetter'
      },
      screenId: {
        type: ID
      },
      dayNumber: {
        type: Integer,
        customGetter: 'dayNumberGetter'
      },

      bookingWeekMovie: {
        type: BookingWeekMovie,
        reference: true
      },

      showing: {
        type: Showing,
        default: null,
        reference: true
      },

      preview: {
        type: Boolean,
        default: false,
        local: true
      },

      currentSelection: {
        type: Boolean,
        default: false,
        local: true
      },

      finalSelection: {
        type: Boolean,
        default: false,
        local: true
      },

      time: {
        type: IsoDateTime,
        default: null,
        local: true
      },

      ghostSlot: {
        type: Boolean,
        default: false,
        local: true
      }
    }

    this.computed = {
      siteTime () {
        if (!this.time) return this.time

        return this.time.inTimeZone(this.configuration.currentSite?.timeZone)
      },
      planned () {
        return !this.showing
      },
      layoutKey () {
        return `${this.screen.id},${this.timeSlot.id},${this.dayNumber}`
      },
      titleNumber () {
        return this.bookingWeekMovie?.priorityOrder + 1
      },
      movie () {
        return this.bookingWeekMovie?.movie
      },
      timeSlotGetter () {
        if (!this.showing) {
          return this.configuration.timeSlots.get(this.timeSlotId) || {}
        }

        const timeSlot = TimeSlot.timeSlotForDateTime(this.showing.siteTime)
        if (!timeSlot) {
          throw new Error('No matching time slot for ' + this.showing.siteTime + ' ' + this.showing.id)
        }

        return timeSlot
      },
      dayNumberGetter () {
        if (!this.showing) {
          return this.data.dayNumber
        }

        return this.showing.dayNumber
      },
      screenGetter () {
        if (!this.showing) {
          return this.configuration.screens.get(this.screenId) || {}
        }

        return this.showing.screen
      },
      selected () {
        return this.currentSelection || this.finalSelection
      },
      planElement () {
        return this.bookingWeekMovie?.planElement
      },
      height () {
        return innerItemHeight
      },
      top () {
        let top

        const screenIndex = this.bookingWeek.screenIndex(this.screen.id)
        const dayIndex = this.bookingWeek.dayIndex(this.dayNumber)

        const screenCount = this.bookingWeek.filteredScreens.length
        const resourceGraphHeight = this.bookingWeek.resourceGraphHeight

        if (this.bookingWeek.by === 'screen') {
          top = headerHeight + screenIndex * 7 * totalItemHeight + dayIndex * totalItemHeight + yMargin
        } else {
          top = headerHeight +
            screenIndex * totalItemHeight +
            dayIndex * (totalItemHeight * screenCount + resourceGraphHeight) +
            resourceGraphHeight +
            yMargin
        }

        if (this.overlaps) {
          top += 10
        }

        return top
      },
      startTimeMinutes () {
        if (this.showing) {
          return timeToMinutes(this.showing.siteTime)
        } else if (this.siteTime) {
          return timeToMinutes(this.siteTime)
        } else {
          return (timeToMinutes(this.timeSlot.targetTime) + this.screen.startOffsetMinutes)
        }
      },
      totalDurationMinutes () {
        return this.showing?.totalDurationMinutes || defaultMovieMinutes
      },
      endTimeMinutes () {
        return this.startTimeMinutes + this.totalDurationMinutes
      },
      left () {
        return this.startTimeMinutes * this.bookingWeek.pixelsPerMinute
      },
      width () {
        return this.totalDurationMinutes * this.bookingWeek.pixelsPerMinute
      },
      finalTime () {
        let time
        if (this.showing) {
          time = this.showing.time
        } else if (this.time) {
          time = this.time
        } else {
          const showingSlotTime = {
            hours: +this.timeSlot.targetTime.split(':')[0],
            minutes: +this.timeSlot.targetTime.split(':')[1]
          }
          const bookingWeekStartWithSlotTime = date.adjustDate(this.bookingWeek.date, showingSlotTime)

          const daysToAdd = _.indexOf(this.configuration.currentCircuit.daysOfWeekNumbers, +this.dayNumber)
          const offsetValues = {
            days: daysToAdd,
            minutes: this.screen.startOffsetMinutes
          }
          const showingTimeInBrowserTimezone = date.addToDate(bookingWeekStartWithSlotTime, offsetValues)
          const siteTimeDifferenceFromBrowserMinutes = this.getOffsetMinutes(this.configuration.currentSite.timeZone, showingTimeInBrowserTimezone) - this.getOffsetMinutes(Intl.DateTimeFormat().resolvedOptions().timeZone, showingTimeInBrowserTimezone)

          time = date.addToDate(showingTimeInBrowserTimezone, { minutes: -siteTimeDifferenceFromBrowserMinutes })
        }
        return time
      },
      startTime () {
        let startTime
        if (this.showing) {
          startTime = this.showing.siteTime
        } else if (this.siteTime) {
          startTime = this.siteTime
        } else {
          startTime = date.adjustDate(new Date(), { hours: +this.timeSlot.targetTime.split(':')[0], minutes: +this.timeSlot.targetTime.split(':')[1] })
          startTime = date.addToDate(startTime, { minutes: this.screen.startOffsetMinutes })
        }

        return date.formatDate(startTime, 'h:mmA').slice(0, -1)
      },
      endTime () {
        let endTime
        if (this.showing) {
          endTime = date.addToDate(this.showing.siteTime, { minutes: this.totalDurationMinutes || defaultMovieMinutes })
        } else if (this.time) {
          endTime = date.addToDate(this.siteTime, { minutes: this.totalDurationMinutes || defaultMovieMinutes })
        } else {
          endTime = date.adjustDate(new Date(), { hours: +this.timeSlot.targetTime.split(':')[0], minutes: +this.timeSlot.targetTime.split(':')[1] })
          endTime = date.addToDate(endTime, { minutes: this.screen.startOffsetMinutes + defaultMovieMinutes })
        }

        return date.formatDate(endTime, 'h:mmA').slice(0, -1)
      },
      postShowingBufferWidth () {
        return this.screen.postShowingBufferMinutes * this.bookingWeek.pixelsPerMinute
      },
      preStartTimeBufferWidth () {
        return this.screen.preStartTimeBufferMinutes * this.bookingWeek.pixelsPerMinute
      },
      movieTermDescription () {
        if (this.showing?.movieTerm) {
          return this.showing.movieTerm.description === 'Default' ? null : this.showing.movieTerm.format
        }

        return null
      },
      priceCard () {
        if (this.showing) {
          return this.showing.computedPriceCard
        } else {
          return (this.movie?.overridePriceCard && this.movie?.customPriceCard) ||
          (this.planElement?.overridePriceCard && this.planElement?.customPriceCard) ||
          this.configuration.currentSite.defaultPriceCard(this.dayNumber, this.timeSlot.id)
        }
      },
      showingBadges () {
        if (this.showing) {
          return this.showing.computedShowingBadges
        } else {
          return _([
            this.movie?.showingBadges?.data,
            this.screen?.showingBadges?.data,
            this.planElement?.showingBadges?.data,
            this.priceCard?.showingBadges?.data
          ]).flatten().compact().uniqBy('id').sortBy('displayOrder').value()
        }
      },
      overlaps () {
        return this.showing && _.some(this.bookingWeek.showings.data, showing => {
          return showing.id > this.showing.id && this.showing.screenId === showing.screenId && this.showing.dayNumber === showing.dayNumber && this.showing.siteTime <= showing.siteEndTime && this.showing.siteEndTime >= showing.siteTime
        })
      }
    }
  }

  isMatch (other) {
    return (
      this === other) || (
      this.layoutKey === other.layoutKey && (
        (!this.bookingWeekMovie && !other.bookingWeekMovies) ||
          this.bookingWeekMovie === other.bookingWeekMovie
      )
    )
  }

  convertToShowing () {
    const showing = new Showing({
      time: this.finalTime,
      screenId: this.screen.id,
      movie: this.bookingWeekMovie.movie,
      bookingWeek: this.bookingWeek,
      bookingWeekMovie: this.bookingWeekMovie
    }, {
      showings: 'default'
    })

    if (this.bookingWeekMovie.movie.isMarathon) {
      let priorEndTime = this.finalTime
      _.each(this.bookingWeekMovie.movie.marathonMovies.data, marathonMovie => {
        const marathonShowing = new Showing({
          time: priorEndTime,
          screenId: this.screen.id,
          movie: marathonMovie,
          bookingWeek: this.bookingWeek
        })

        showing.showings.add(marathonShowing)
        priorEndTime = marathonShowing.siteEndTime
      })
    }

    return showing
  }

  getOffsetMinutes (timeZone = 'UTC', date = new Date()) {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }))
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone }))
    return (tzDate.getTime() - utcDate.getTime()) / 60 / 1000
  }
}
